import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Stores.module.scss';
import { getLocais } from '../../services/api.tsx';

import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import PoolOutlinedIcon from "@mui/icons-material/PoolOutlined";
import LocalParkingOutlinedIcon from "@mui/icons-material/LocalParkingOutlined";
import WifiOutlinedIcon from "@mui/icons-material/WifiOutlined";

type Categoria = 'restaurantes' | 'hoteis';
export interface IEstabelecimento {
  id: number;
  nome: string;
  tipo: string;
  cidade: string;
  descricao: string;
  avaliacao: number;
  avaliacoes: number;
  imagem: string;
  galeria?: string[];
  endereco?: string;
  telefone?: string;
  horario?: string;
  preco?: string;
  sobre?: string;
  destaques?: string[];
  tags?: string[];
}

const normalizeLocalToStore = (local: any): IEstabelecimento => {
  const categorias = Array.isArray(local.categoria)
    ? local.categoria.map((c: { nomeCategoria: any; nome: any; }) =>
        typeof c === 'string' ? c : c?.nomeCategoria || c?.nome || '',
      )
    : typeof local.categoria === 'string'
    ? [local.categoria]
    : [];

  const tipo = categorias.some((n: string) => /hotel|pousada|hospedagem/i.test(n))
    ? 'Hotel'
    : categorias.some((n: string) => /restaurante|comida|gastronom|bar|lanchonete/i.test(n))
    ? 'Restaurante'
    : 'Estabelecimento';

  const normalizePhotoString = (photo: any): string | undefined => {
    if (!photo) return undefined;
    if (typeof photo === 'string') {
      const trimmed = photo.trim();
      if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('/')) {
        return trimmed;
      }
      return `/${trimmed.replace(/^\/+/, '')}`;
    }
    const url = photo?.url || photo?.path || photo?.src || photo?.imagem;
    if (!url) return undefined;
    return normalizePhotoString(url);
  };

  const imagemPrincipal = Array.isArray(local.fotos) && local.fotos.length
    ? normalizePhotoString(local.fotos[0])
    : typeof local.imagem === 'string'
    ? normalizePhotoString(local.imagem)
    : undefined;

  return {
    id: Number(local.id) || 0,
    nome: local.nome || 'Estabelecimento',
    tipo,
    cidade:
      local.cidade ||
      (typeof local.endereco === 'string'
        ? local.endereco.split(',')[1]?.trim() || 'Sergipe'
        : 'Sergipe'),
    descricao: local.descricao || local.sobre || '',
    avaliacao: Number(local.avaliacao) || 0,
    avaliacoes: Number(local.avaliacoes) || 0,
    imagem: imagemPrincipal || '/assets/placeholder.jpg',
    galeria:
      Array.isArray(local.fotos) && local.fotos.length
        ? local.fotos.map(normalizePhotoString).filter(Boolean) as string[]
        : typeof local.imagem === 'string'
        ? [normalizePhotoString(local.imagem)!]
        : ['/assets/placeholder.jpg'],
    endereco: local.endereco || '',
    telefone: local.telefone || '',
    horario: local.horario || '',
    preco: local.preco || '',
    sobre: local.sobre || local.descricao || '',
    destaques: local.destaques || [],
    tags: categorias.filter(Boolean),
  };
};

const categoryMatches = (
  item: IEstabelecimento,
  categoriaAtiva: Categoria,
  categoriaBusca: string,
) => {
  const textoReferencia = `${item.tipo} ${item.descricao} ${item.tags?.join(' ')}`.toLowerCase();
  const buscaCategoria = categoriaBusca.toLowerCase();

  const tipoMatch =
    categoriaAtiva === 'restaurantes'
      ? /restaurante|comida|gastronom|bar|lanchonete|estabelecimento/i.test(textoReferencia)
      : /hotel|pousada|hospedagem|inn/i.test(textoReferencia);

  const categoriaSelecionada =
    categoriaBusca === 'Todas as Categorias' || categoriaBusca.trim() === ''
      ? true
      : textoReferencia.includes(buscaCategoria) ||
        item.tipo.toLowerCase().includes(buscaCategoria) ||
        item.tags?.some((tag) => tag.toLowerCase().includes(buscaCategoria));

  return tipoMatch && categoriaSelecionada;
};

const Stores: React.FC = () => {
  const navigate = useNavigate();
  const [categoriaAtiva, setCategoriaAtiva] = useState<Categoria>('restaurantes');
  const [busca, setBusca] = useState('');
  const [categoriaBusca, setCategoriaBusca] = useState('Todas as Categorias');
  const [stores, setStores] = useState<IEstabelecimento[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStores = async () => {
      try {
        const locais = await getLocais();
        setStores(locais.map(normalizeLocalToStore));
      } catch (error) {
        console.error('Erro ao carregar estabelecimentos:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStores();
  }, []);

  if (loading) {
    return (
      <section className={styles.storesPage}>
        <p>Carregando estabelecimentos...</p>
      </section>
    );
  }

  const dadosAtuais = stores.filter((item) =>
    categoryMatches(item, categoriaAtiva, categoriaBusca),
  );

  const dadosFiltrados = dadosAtuais.filter((item) => {
    const textoBusca = busca.toLowerCase();
    return (
      item.nome.toLowerCase().includes(textoBusca) ||
      item.cidade.toLowerCase().includes(textoBusca) ||
      item.descricao.toLowerCase().includes(textoBusca) ||
      item.tags?.some((tag) => tag.toLowerCase().includes(textoBusca))
    );
  });

  const bannerAtual =
    categoriaAtiva === 'restaurantes'
      ? '/assets/banner-restaurantes.jpg'
      : '/assets/banner-hoteis.jpg';

  return (
    <section className={styles.storesPage}>
      <section
        className={styles.hero}
        style={{ backgroundImage: `url(${bannerAtual})` }}
      >
        <div className={styles.heroOverlay}>
          <div className={styles.heroText}>
            <span>Sergipanidade Turismo</span>
            <h1>
              {categoriaAtiva === 'restaurantes'
                ? 'Descubra sabores de Sergipe'
                : 'Encontre sua hospedagem ideal'}
            </h1>
            <p>
              {categoriaAtiva === 'restaurantes'
                ? 'Explore restaurantes, comidas típicas e experiências gastronômicas pelo estado.'
                : 'Veja hotéis e pousadas para descansar e aproveitar melhor sua estadia.'}
            </p>
          </div>

          <div className={styles.searchBox}>
            <div className={styles.searchField}>
              <LocationOnOutlinedIcon className={styles.searchIcon} />
              <input
                type="text"
                placeholder={
                  categoriaAtiva === 'restaurantes'
                    ? 'Digite o nome do restaurante'
                    : 'Digite o nome do hotel'
                }
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </div>

            <div className={styles.selectField}>
              <CategoryOutlinedIcon className={styles.searchIcon} />
              <select
                value={categoriaBusca}
                onChange={(e) => setCategoriaBusca(e.target.value)}
              >
                <option>Todas as Categorias</option>
                <option>Restaurantes</option>
                <option>Hotéis</option>
                <option>Pousadas</option>
                <option>Comida típica</option>
              </select>
            </div>

            <button className={styles.searchButton}>
              <SearchIcon sx={{ fontSize: 18 }} />
              Buscar
            </button>
          </div>

          <div className={styles.tabs}>
            <button
              className={
                categoriaAtiva === 'restaurantes'
                  ? `${styles.tabButton} ${styles.activeTab}`
                  : styles.tabButton
              }
              onClick={() => setCategoriaAtiva('restaurantes')}
            >
              Restaurantes
            </button>
            <button
              className={
                categoriaAtiva === 'hoteis'
                  ? `${styles.tabButton} ${styles.activeTab}`
                  : styles.tabButton
              }
              onClick={() => setCategoriaAtiva('hoteis')}
            >
              Hotéis
            </button>
          </div>
        </div>
      </section>

      <section className={styles.content}>
        <div className={styles.titleRow}>
          <h1>
            {categoriaAtiva === 'restaurantes'
              ? 'Restaurantes típicos'
              : 'Hotéis e Pousadas'}
          </h1>
        </div>
        <div className={styles.pageIntro}>
          <p>
            {categoriaAtiva === 'restaurantes'
              ? 'Explore restaurantes selecionados em Sergipe, com opções de comida típica, ambientes familiares e experiências gastronômicas para diferentes estilos de viagem.'
              : 'Encontre hotéis e pousadas em Sergipe com boas avaliações, localização estratégica e opções para lazer, descanso ou viagens rápidas.'}
          </p>
        </div>

        <div className={styles.cardsGrid}>
          {dadosFiltrados.map((item) => (
            <article className={styles.card} key={item.id}>
              <img src={item.imagem} alt={item.nome} className={styles.cardImage} />
              <div className={styles.cardBody}>
                <h2>{item.nome}</h2>
                <p className={styles.type}>{item.tipo}</p>
                {categoriaAtiva === 'hoteis' && (
                  <div className={styles.amenities}>
                    <WifiOutlinedIcon sx={{ fontSize: 16 }} />
                    <PoolOutlinedIcon sx={{ fontSize: 16 }} />
                    <LocalParkingOutlinedIcon sx={{ fontSize: 16 }} />
                  </div>
                )}
                <div className={styles.rating}>
                  <div>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <StarIcon
                        key={star}
                        sx={{ fontSize: 15 }}
                        className={
                          star <= Math.round(item.avaliacao)
                            ? styles.starActive
                            : styles.starInactive
                        }
                      />
                    ))}
                  </div>
                  <span>
                    {item.avaliacao.toFixed(1)} · {item.avaliacoes} avaliações
                  </span>
                </div>
                <p className={styles.location}>
                  {item.cidade} | {item.descricao}
                </p>
                <button
                  className={styles.detailsButton}
                  onClick={() => navigate(`/estabelecimentos/${item.id}`)}
                >
                  Ver detalhes
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
};

export default Stores;
